import {Search} from '@mui/icons-material';
import {debounce, OutlinedInput} from '@mui/material';
import {ChangeEvent, FC, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {systemApi} from 'src/api/system';
import {useAppDispatch} from 'src/app/hooks';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {FilterBar, FilterBarField} from 'src/components/common/FilterBar';
import {handleGetCountries} from 'src/hooks/useGetCountries';
import {searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {TariffsFilterValue, TariffsTableActionsType} from './TariffsTableActions.types';

const ALL = '';

/**
 * The tariffs toolbar.
 *
 * Regions belong to a country, so the second select is only meaningful once the first is set, and
 * changing the country clears it — a region under the old country would filter to nothing.
 */
export const TariffsTableActions: FC<TariffsTableActionsType> = ({
                                                                    handleSearch,
                                                                    handleFilter,
                                                                    filters,
                                                                    title,
                                                                    icon,
                                                                    onAdd,
                                                                    addLabel,
                                                                    totalElements,
                                                                }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [getCountries, countries] = systemApi.useLazyGetCountriesQuery();

    // Rendered from the filter the list is actually on, not from a copy of its own.
    const country = filters.country ?? ALL;
    const region = filters.region ?? ALL;

    useEffect(() => {
        void handleGetCountries(getCountries, dispatch);
    }, []);

    // Only what changed: the table merges it onto the filter the list is on.
    const apply = (next: TariffsFilterValue) => handleFilter(next);

    const countryOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allCountries'}), value: ALL},
        ...(countries?.data ?? []).map((item) => ({
            id: item.id,
            name: intl.formatMessage({id: item.name.toLowerCase()}),
            value: item.name,
        })),
    ], [countries?.data, intl]);

    const regionOptions = useMemo(() => {
        const selected = (countries?.data ?? []).find((item) => item.name === country);
        return [
            {id: 0, name: intl.formatMessage({id: 'allRegions'}), value: ALL},
            ...(selected?.regions ?? []).map((item) => ({
                id: item.id,
                name: intl.formatMessage({id: item.name}),
                value: item.name,
            })),
        ];
    }, [countries?.data, country, intl]);

    const fields: FilterBarField[] = [
        {
            id: 'country',
            control: (
                <AMPFormSelect
                    dense
                    data={countryOptions}
                    value={country}
                    input="country"
                    onChange={(e) => apply({country: String(e.target.value), region: ALL})}
                />
            ),
        },
        {
            id: 'region',
            control: (
                <AMPFormSelect
                    dense
                    data={regionOptions}
                    value={region}
                    input="region"
                    disabled={!country}
                    onChange={(e) => apply({region: String(e.target.value)})}
                />
            ),
        },
    ];

    return (
        <FilterBar
            title={title}
            icon={icon}
            onAdd={onAdd}
            addLabel={addLabel}
            subtitle={typeof totalElements === 'number'
                ? intl.formatMessage({id: 'rowsFound'}, {count: totalElements})
                : undefined}
            fields={fields}
            hasActiveFilters={Boolean(country || region)}
            onClearAll={() => apply({country: ALL, region: ALL})}
            actions={(
                <OutlinedInput
                    sx={searchInputSx}
                    color="secondary"
                    type="text"
                    placeholder={intl.formatMessage({id: 'search'})}
                    name="search"
                    // Uncontrolled for the debounce, but seeded: the box has to say what the list is
                    // filtered by after a Back, which remounts it with the keyword still in the URL.
                    defaultValue={filters?.keyword ?? ''}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                        debounce(() => handleSearch(event.target.value), 1000)()}
                    startAdornment={<Search/>}
                />
            )}
        />
    );
};
